import coreAr from "@/locales/ar.json";
import coreEn from "@/locales/en.json";
import billingAr from "@/locales/billing.ar.json";
import billingEn from "@/locales/billing.en.json";
import calendarAr from "@/locales/calendar.ar.json";
import calendarEn from "@/locales/calendar.en.json";
import filesAr from "@/locales/files.ar.json";
import filesEn from "@/locales/files.en.json";
import hrAr from "@/locales/hr.ar.json";
import hrEn from "@/locales/hr.en.json";
import leadsAr from "@/locales/leads.ar.json";
import leadsEn from "@/locales/leads.en.json";
import peopleAr from "@/locales/people.ar.json";
import peopleEn from "@/locales/people.en.json";
import planningAr from "@/locales/planning.ar.json";
import portalAr from "@/locales/portal.ar.json";
import portalEn from "@/locales/portal.en.json";
import planningEn from "@/locales/planning.en.json";
import ticketsAr from "@/locales/tickets.ar.json";
import ticketsEn from "@/locales/tickets.en.json";
import timeAr from "@/locales/time.ar.json";
import timeEn from "@/locales/time.en.json";
import workAr from "@/locales/work.ar.json";
import workEn from "@/locales/work.en.json";
import workspaceAr from "@/locales/workspace.ar.json";
import workspaceEn from "@/locales/workspace.en.json";
import attendanceAr from "@/locales/attendance.ar.json";
import attendanceEn from "@/locales/attendance.en.json";
import requestsAr from "@/locales/requests.ar.json";
import requestsEn from "@/locales/requests.en.json";
import timeOffAr from "@/locales/timeOff.ar.json";
import timeOffEn from "@/locales/timeOff.en.json";
import payrollAr from "@/locales/payroll.ar.json";
import performanceAr from "@/locales/performance.ar.json";
import performanceEn from "@/locales/performance.en.json";
import recruitmentAr from "@/locales/recruitment.ar.json";
import recruitmentEn from "@/locales/recruitment.en.json";
import engagementAr from "@/locales/engagement.ar.json";
import engagementEn from "@/locales/engagement.en.json";
import reportsAr from "@/locales/reports.ar.json";
import reportsEn from "@/locales/reports.en.json";
import payrollEn from "@/locales/payroll.en.json";
import lifecycleAr from "@/locales/lifecycle.ar.json";
import lifecycleEn from "@/locales/lifecycle.en.json";

/* One catalog per area so features grow without stepping on each other: core (shell, auth,
   shared), people (team, clients, invitations, profile), work (projects, board, tasks) and
   workspace (home, my work, notifications, comments and activity, search), planning (phases,
   milestones, subtasks, labels), time (timer, timesheet, reports), files, hr (records,
   directory, org chart, my HR), attendance, requests (the approval inbox), timeOff, payroll,
   lifecycle (checklists, assets, letters), performance, recruitment,
   engagement (announcements, policies, surveys, celebrations) and reports (people reports). Ids are unique across all of them
   (catalogs.test.ts). */
export const CATALOGS = {
  core: { en: coreEn, ar: coreAr },
  people: { en: peopleEn, ar: peopleAr },
  work: { en: workEn, ar: workAr },
  workspace: { en: workspaceEn, ar: workspaceAr },
  planning: { en: planningEn, ar: planningAr },
  time: { en: timeEn, ar: timeAr },
  files: { en: filesEn, ar: filesAr },
  portal: { en: portalEn, ar: portalAr },
  billing: { en: billingEn, ar: billingAr },
  leads: { en: leadsEn, ar: leadsAr },
  calendar: { en: calendarEn, ar: calendarAr },
  tickets: { en: ticketsEn, ar: ticketsAr },
  hr: { en: hrEn, ar: hrAr },
  attendance: { en: attendanceEn, ar: attendanceAr },
  requests: { en: requestsEn, ar: requestsAr },
  timeOff: { en: timeOffEn, ar: timeOffAr },
  payroll: { en: payrollEn, ar: payrollAr },
  performance: { en: performanceEn, ar: performanceAr },
  recruitment: { en: recruitmentEn, ar: recruitmentAr },
  engagement: { en: engagementEn, ar: engagementAr },
  reports: { en: reportsEn, ar: reportsAr },
  lifecycle: { en: lifecycleEn, ar: lifecycleAr },
} as const;

const en = {
  ...coreEn,
  ...peopleEn,
  ...workEn,
  ...workspaceEn,
  ...planningEn,
  ...timeEn,
  ...filesEn,
  ...portalEn,
  ...billingEn,
  ...leadsEn,
  ...calendarEn,
  ...ticketsEn,
  ...hrEn,
  ...attendanceEn,
  ...requestsEn,
  ...timeOffEn,
  ...payrollEn,
  ...performanceEn,
  ...recruitmentEn,
  ...engagementEn,
  ...reportsEn,
  ...lifecycleEn,
};
const ar = {
  ...coreAr,
  ...peopleAr,
  ...workAr,
  ...workspaceAr,
  ...planningAr,
  ...timeAr,
  ...filesAr,
  ...portalAr,
  ...billingAr,
  ...leadsAr,
  ...calendarAr,
  ...ticketsAr,
  ...hrAr,
  ...attendanceAr,
  ...requestsAr,
  ...timeOffAr,
  ...payrollAr,
  ...performanceAr,
  ...recruitmentAr,
  ...engagementAr,
  ...reportsAr,
  ...lifecycleAr,
};

export type Locale = "ar" | "en";
export type MessageId = keyof typeof en;

export const LOCALES: readonly Locale[] = ["ar", "en"];
export const DEFAULT_LOCALE: Locale = "ar";

export const MESSAGES: Record<Locale, Record<MessageId, string>> = { ar, en };

export function directionOf(locale: Locale): "rtl" | "ltr" {
  return locale === "ar" ? "rtl" : "ltr";
}

/** Intl locale tag: Egyptian Arabic formatting for "ar", British English for "en". */
export function intlLocaleOf(locale: Locale): string {
  return locale === "ar" ? "ar-EG" : "en-GB";
}

/** Whether a string is a catalog message id, such as a validation message stored on a form. */
export function isMessageId(value: string): value is MessageId {
  return Object.hasOwn(en, value);
}

export function isLocale(value: unknown): value is Locale {
  return value === "ar" || value === "en";
}
